import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBars, FaBell, FaSignOutAlt, FaCheck, FaChevronDown, FaUser, FaUserPlus, FaComments, FaFileInvoice } from 'react-icons/fa';
import io from 'socket.io-client';
import { useToast } from '../context/ToastContext';
import { useEmployee } from '../context/EmployeeContext';
import Breadcrumbs from './ui/Breadcrumbs';
import ThemeCustomizer from './ui/ThemeCustomizer';

const timeAgo = (date) => {
  const now = new Date();
  const diff = Math.floor((now - new Date(date)) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString();
};

const EmployeeHeader = ({ setSidebarOpen }) => {
  const [notifications, setNotifications] = useState([]);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const navigate = useNavigate();
  const notifDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const socketRef = useRef(null);
  const toast = useToast();
  const { employee } = useEmployee();

  const displayName = employee?.name?.trim() || 'Employee';
  const employeeInitial = displayName[0]?.toUpperCase() || 'E';
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const fetchNotifications = useCallback(async () => {
    const token = localStorage.getItem('employeeToken');
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch notifications.');
      const data = await response.json();
      setNotifications(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching notifications:', error.message);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    if (!employee?._id) return undefined;

    socketRef.current = io(process.env.REACT_APP_API_BASE_URL);
    socketRef.current.emit('joinEmployeeRoom', employee._id);

    socketRef.current.on('newNotification', (notification) => {
      const allowedTypes = ['client_assignment', 'client_removal', 'chat', 'document'];
      if (notification?.forEmployee && allowedTypes.includes(notification?.type)) {
        setNotifications((prev) => [notification, ...prev.filter((n) => n._id !== notification._id)]);

        // Show toast notification if appropriate
        const isChat = notification.type === 'chat';
        const isChatOpen = document.getElementById('employee-chatbox') !== null;

        // Only show chat toast if the chatbox is NOT open
        if (!isChat || !isChatOpen) {
          const title = notification.title || 'New Notification';
          const msg = notification.message || '';
          const combined = msg ? `${title}: ${msg}` : title;
          if (isChat) {
            toast.info(`💬 ${combined}`);
          } else if (notification.type === 'document') {
            toast.info(`📎 ${combined}`);
          } else {
            toast.success(`🔔 ${combined}`);
          }
        }
      }
    });

    return () => {
      socketRef.current?.disconnect();
    };
  }, [employee?._id]);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('employeeToken');
      if (token) {
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (error) {
      console.error('Error recording employee logout:', error);
    } finally {
      localStorage.removeItem('employeeToken');
      setShowProfileDropdown(false);
      toast.success('Logged out successfully');
      navigate('/login');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notifDropdownRef.current
        && !notifDropdownRef.current.contains(event.target)
        && !event.target.closest('.notification-btn')
      ) {
        setShowNotifDropdown(false);
      }

      if (
        profileDropdownRef.current
        && !profileDropdownRef.current.contains(event.target)
        && !event.target.closest('.profile-menu-btn')
      ) {
        setShowProfileDropdown(false);
      }
    };

    if (showNotifDropdown || showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifDropdown, showProfileDropdown]);

  const handleNotificationClick = async (notif) => {
    const token = localStorage.getItem('employeeToken');
    setShowNotifDropdown(false);

    if (!notif.isRead && notif._id && token) {
      try {
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/notifications/mark-read/${notif._id}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        setNotifications((prev) => prev.map((n) => (n._id === notif._id ? { ...n, isRead: true } : n)));
      } catch (error) {
        console.error('Error marking notification as read:', error.message);
      }
    }

    navigate(notif.link || '/client');
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('employeeToken');
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/notifications/mark-all-read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setShowNotifDropdown(false);
      toast.info('All notifications marked as read');
    } catch (error) {
      console.error('Error marking notifications as read:', error.message);
      toast.error('Failed to mark notifications as read');
    }
  };

  return (
    <header className="sticky top-0 z-30 h-16 bg-gradient-to-r from-black via-neutral-900 to-primary-900 text-white dark:from-black dark:via-black dark:to-neutral-950 backdrop-blur-xl border-b border-white/10 dark:border-neutral-800 shadow-lg transition-colors duration-300">
      <div className="flex items-center justify-between h-full px-4 md:px-6">
        <div className="flex items-center gap-4">
          <button
            className="lg:hidden p-2 rounded-xl text-white hover:bg-white/10 transition-all duration-200"
            onClick={() => setSidebarOpen(true)}
          >
            <FaBars className="w-5 h-5" />
          </button>

          <div className="hidden sm:flex items-center text-sm">
            <Breadcrumbs variant="header" />
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-4 text-white">
          <ThemeCustomizer />

          <div className="relative">
            <button
              className="notification-btn relative p-2.5 rounded-xl bg-white/10 text-white dark:bg-neutral-900 dark:text-gray-300 hover:bg-white/20 dark:hover:bg-neutral-800 transition-all duration-200"
              onClick={() => {
                setShowProfileDropdown(false);
                setShowNotifDropdown((prev) => !prev);
              }}
            >
              <FaBell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-bold text-white bg-gradient-to-r from-red-500 to-rose-600 rounded-full shadow-lg animate-pulse">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifDropdown && (
              <div
                ref={notifDropdownRef}
                className="absolute -right-14 sm:right-0 mt-2 w-[300px] sm:w-96 bg-white dark:bg-neutral-900 rounded-2xl shadow-3d border border-gray-100 dark:border-neutral-800 overflow-hidden animate-fade-in z-50"
              >
                <div className="px-4 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">Notifications</h3>
                    <p className="text-xs text-white/70">{unreadCount} unread</p>
                  </div>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors"
                    >
                      <FaCheck className="w-3 h-3" />
                      Mark all read
                    </button>
                  )}
                </div>

                <div className="max-h-80 overflow-y-auto">
                  {notifications.length > 0 ? (
                    notifications.slice(0, 20).map((notif) => {
                      const getNotificationIcon = (type) => {
                        if (type === 'chat') return <FaComments className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />;
                        if (type === 'document') return <FaFileInvoice className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />;
                        return <FaUserPlus className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />;
                      };

                      return (
                        <button
                          key={notif._id}
                          type="button"
                          onClick={() => handleNotificationClick(notif)}
                          className={`w-full text-left px-4 py-3 border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors ${!notif.isRead ? 'bg-primary-50/50 dark:bg-primary-900/10' : ''
                            }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center shrink-0 mt-0.5">
                              {getNotificationIcon(notif.type)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {notif.title || 'Client Assigned'}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">{notif.message}</p>
                              {notif.createdAt && (
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{timeAgo(notif.createdAt)}</p>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="px-4 py-8 text-center">
                      <FaBell className="w-8 h-8 text-gray-300 dark:text-neutral-700 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">No notifications found</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setShowNotifDropdown(false);
                setShowProfileDropdown((prev) => !prev);
              }}
              className="profile-menu-btn flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-all border border-white/10"
              title="Account menu"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-400 to-secondary-500 flex items-center justify-center overflow-hidden shrink-0 text-sm font-semibold">
                {employee?.profileImage ? (
                  <img src={employee.profileImage} alt={displayName} className="w-full h-full object-cover" />
                ) : (
                  employeeInitial
                )}
              </div>
              <div className="hidden md:block text-left min-w-0 max-w-[140px]">
                <p className="text-sm font-medium truncate leading-tight">{displayName}</p>
                <p className="text-[10px] text-white/60 truncate leading-tight">Employee</p>
              </div>
              <FaChevronDown className={`w-3 h-3 text-white/70 hidden sm:block transition-transform ${showProfileDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showProfileDropdown && (
              <div
                ref={profileDropdownRef}
                className="absolute right-0 mt-2 w-64 bg-white dark:bg-neutral-900 rounded-2xl shadow-3d border border-gray-100 dark:border-neutral-800 overflow-hidden animate-fade-in z-50"
              >
                <div className="px-4 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center overflow-hidden text-lg font-bold shrink-0">
                      {employee?.profileImage ? (
                        <img src={employee.profileImage} alt={displayName} className="w-full h-full object-cover" />
                      ) : (
                        employeeInitial
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{displayName}</p>
                      <p className="text-xs text-white/75 truncate">{employee?.email || 'Employee Account'}</p>
                    </div>
                  </div>
                </div>

                <div className="py-1">
                  <button
                    type="button"
                    onClick={() => {
                      setShowProfileDropdown(false);
                      navigate('/profile');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors"
                  >
                    <FaUser className="w-4 h-4 text-primary-500" />
                    <span>My Profile</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors border-t border-gray-100 dark:border-neutral-800"
                  >
                    <FaSignOutAlt className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default EmployeeHeader;
